<template>
  <div class="vap-full-screen api-lookup-management">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-fs-header">
      <h1>API Lookups Management</h1>
      <div class="btns-container">
        <VasionButton classProp="secondary" @vasionButtonClicked="cancel()">
          Cancel
        </VasionButton>
        <VasionButton
          classProp="primary"
          :isDisabled="!isDirty"
          @vasionButtonClicked="save()"
        >
          Save
        </VasionButton>
      </div>
    </div>
    <div class="vap-fs-body bg-surface">
      <ApiLookupSectionedForm
        ref="apiLookupForm"
        :objectID="objectID"
        @markAsDirty="markAsDirty"
        @sendTestRequestData="sendTestRequestData"
        @syncTestRequestData="syncTestRequestData"
        @sendExampleRequestData="sendExampleRequestData"
        @showSnackbar="showSnackbar"
      />
    </div>
    <VasionSnackbar
      id="snackbar-apilookup-management"
      ref="apiLookupManagementSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import ApiLookupSectionedForm from '@/components/forms/api/ApiLookupSectionedForm.vue'
import * as HTTPMethod from '@/static-data/apiLookupHttpMethods.json'

export default {
  name: 'ApiLookupManagement',
  components: {
    Loading,
    ApiLookupSectionedForm,
  },
  data() {
    return {
      apiLookupId: this.$route.params.apiLookupId,
      isDirty: false,
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      payload: {},
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
      testData: {},
    }
  },
  computed: {
    isEdit() {
      return this.apiLookupId ? true : false
    },
    objectID() {
      return String(this.$route.params.formID)
    },
    newFormId() {
      return this.$store.state.attributeForm.newFormId
    },
  },
  async created() {
    this.payload = {
      GroupID: this.isEdit ? this.apiLookupId : 0,
      ApiLookupName: '',
      IndexFormId: Number(this.objectID),
      HTTPMethod: '',
      ApiURL: '',
      ApiRequestBodyType: 0,
      ApiRequestBody: '',
      ApiResponse: {},
      IsDefault: false,
      FirstRecordOnly: false,
      ApiAuthenticationId: '',
      Headers: [],
      Parameters: [],
      ApiRequestBodyFormData: [],
    }

    if (this.isEdit && this.apiLookupId) {
      const editableData = await this.getSingleRegistry(this.apiLookupId)
      await this.setRegistryDataInPayload(editableData)
    }
  },
  methods: {
    async cancel() {
      await this.$store.dispatch('forms/setDefaultFormTab', 'api')
      this.$router.push({ name: 'EditForm', params: { formID: this.objectID } })
    },
    checkIfFormIsReadyToBeSaved() {
      // Check Body Section
      if (
        this.payload.ApiRequestBodyType === 'Text' ||
        this.payload.ApiRequestBodyType === 'JSON' ||
        this.payload.ApiRequestBodyType === 'HTML' ||
        this.payload.ApiRequestBodyType === 'XML'
      ) {
        if (!this.payload.ApiRequestBody) {
          this.showSnackbar(
            'API Lookup',
            `Please provide a request body for ${this.payload.ApiRequestBodyType} type`,
            false
          )
          return false
        }
        switch (this.payload.ApiRequestBodyType) {
          case 'JSON':
            try {
              let input = this.payload.ApiRequestBody.replace(/{{([\s\S]*?)}}/g, "0")
              JSON.parse(input)
            } catch (e) {
              this.showSnackbar(
                'API Lookup',
                `Please provide a valid JSON`,
                false
              )
              return false
            }
            break
          case 'HTML': {
            const parser = new DOMParser()
            const html = parser.parseFromString(this.payload.ApiRequestBody, 'text/html')
            const normalizeHtml = (htmlString) => {
              return htmlString
                .replace(/\s+/g, '') 
                .replace(/<!--[\s\S]*?-->/g, '')
                .replace(/<(\w+)\s*\/>/g, '<$1>') 
                .replace(/<\/?(html|head|body)>/g, '') 
                .replace(/<!DOCTYPEhtml>/i, '')
                .trim() 
            }
            const originalHtml = normalizeHtml(this.payload.ApiRequestBody)
            const parsedHtml = normalizeHtml(html.documentElement.outerHTML)
            if (originalHtml !== parsedHtml) {
              this.showSnackbar(
                'API Lookup',
                `Please provide a valid HTML`,
                false
              )
              return false
            }
            break
          }
          case 'XML': {
            const parser = new DOMParser()
            const xml = parser.parseFromString(
              this.payload.ApiRequestBody,
              'text/xml'
            )
            const parserError = xml.getElementsByTagName('parsererror')
            if (parserError.length > 0) {
              this.showSnackbar(
                'API Lookup',
                `Please provide a valid XML`,
                false
              )
              return false
            }
            break
          }
        }
        this.payload.ApiRequestBodyFormData = []
      } else if (this.payload.ApiRequestBodyType === 'Form') {
        if (!this.payload.ApiRequestBodyFormData.length) {
          this.showSnackbar(
            'API Lookup',
            'Please provide a request body for Form Data type',
            false
          )
          return false
        }
        let hasEmptyData = false
        for (let i = 0; i < this.payload.ApiRequestBodyFormData.length; i++) {
          const formData = this.payload.ApiRequestBodyFormData[i]
          if (
            formData.FormDataKey === '' ||
            (formData.FormValue === '' && formData.FormDataType !== 2)
          ) {
            this.showSnackbar(
              'API Lookup',
              'Each form data entry must have a key and a value.',
              false
            )
            hasEmptyData = true
            break
          }
        }

        if (hasEmptyData) return false
        this.payload.ApiRequestBody = ''
      }

      return true
    },
    async getSingleRegistry(id) {
      this.isLoading = true

      try {
        const response = await this.$store.dispatch('admin/getAPILookup', id)

        if (response) {
          if (response.ResultMessage) {
            this.showSnackbar('API Lookup', response.ResultMessage, false)
            setTimeout(() => {
              this.cancel()
            }, 3000)
          }
          const data = response
          this.$refs.apiLookupForm.setFormFields(data)
          return data
        }
      } catch (error) {
        this.showSnackbar('API Lookup', 'An unexpected error occurred', false)
        return {}
      } finally {
        this.isLoading = false
      }
    },
    getUrlWithoutParams(url) {
      if (typeof url !== 'string') return ""

      try {
        const urlObj = new URL(url)
        return urlObj.origin + urlObj.pathname
      } catch {
      return url
      }
    },
    markAsDirty(data) {
      this.isDirty = true
      this.setFieldsInSavingPayload(data)
    },
    async save() {
      this.isLoading = true

      const { method, name, url } = this.$refs.apiLookupForm.formFields

      if (!this.validateSave(method, name, url)) {
        this.isLoading = false
        return
      }

      this.payload = {
        ...this.payload,
        ApiLookupName: name,
        HTTPMethod: method.value,
        ApiURL: this.getUrlWithoutParams(url),
      }

      const formReadyToBeSaved = this.checkIfFormIsReadyToBeSaved()
      if (!formReadyToBeSaved) {
        this.isLoading = false
        return
      }

      this.payload = this.cleanPayload(this.payload)
      try {
        const response = await this.$store.dispatch(
          'admin/saveAPILookup',
          this.payload
        )
        const success =
          response && response?.Value.toLowerCase() === 'false' ? false : true
        this.showSnackbar(
          'API Lookup',
          success
            ? 'API Lookup Successfully Saved'
            : response?.ResultMessage || 'An error occurred during save',
          success
        )
        if (success) {
          this.isDirty = false
          if (!this.apiLookupId) {
            const responseData = this.$refs.apiLookupForm.response
            const responseStats = this.$refs.apiLookupForm.responseStats
            const testData = this.$refs.apiLookupForm.testData

            this.$router.push({
              name: 'EditApiLookup',
              params: {
                formID: this.objectID,
                apiLookupId: response.Value,
                responseData: responseData,
                responseStats: responseStats,
                testData: testData,
              },
            })
          }
        }
      } catch (error) {
        console.error('API Lookup Save Error:', error)
        this.showSnackbar('API Lookup', 'An unexpected error occurred', false)
      } finally {
        this.isLoading = false
      }
    },
    setFieldsInSavingPayload(data) {
      if (!data?.section || !data?.data) return
      if (data.section === 'settings') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          IsDefault: fields.defaultLookup,
          FirstRecordOnly: fields.firstRecordOnly,
        }
      } else if (data.section === 'auth') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          ApiAuthenticationId: fields.apiAuthentication.id,
        }
      } else if (data.section === 'params') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          Parameters: fields.Parameters,
        }
      } else if (data.section === 'headers') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          Headers: fields.Headers,
        }
      } else if (data.section === 'body') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          ApiRequestBodyType: fields.ApiRequestBodyType,
          ApiRequestBody: fields.ApiRequestBody,
          ApiRequestBodyFormData: fields.ApiRequestBodyFormData,
        }
      } else if (data.section === 'mapping') {
        let fields = data.data
        this.payload = {
          ...this.payload,
          ApiResponse: fields.mappingData,
        }
      }
    },
    cleanPayload(payload)
    {
      if (payload.HTTPMethod === HTTPMethod.GET || payload.HTTPMethod === HTTPMethod.DELETE) {
        payload.ApiRequestBodyType = 0
        payload.ApiRequestBody = ''
        payload.ApiRequestBodyFormData = []
      }
      return payload
    },
    async sendExampleRequestData() {
      this.isLoading = true

      const { method, name, url } = this.$refs.apiLookupForm.formFields

      this.payload = {
        ...this.payload,
        ApiLookupName: name,
        HTTPMethod: method.value,
        ApiURL: this.getUrlWithoutParams(url),
      }

      let payload = {
        ...this.payload,
        DocumentId: this.testData?.documentId || 0,
        SampleData: this.testData?.sampleData || {},
      }

      payload = this.cleanPayload(payload)

      const result = await this.$store.dispatch(
        'admin/testExampleRequestAPILookup',
        payload
      )
      if (!result) {
        this.showSnackbar(
          'API Lookup',
          'An unexpected error occurred while getting example data',
          false
        )
        return
      }
      this.$refs.apiLookupForm.setExampleRequestData(result)

      this.isLoading = false
    },
    async sendTestRequestData() {
      this.isLoading = true

      const { method, name, url } = this.$refs.apiLookupForm.formFields

      this.payload = {
        ...this.payload,
        ApiLookupName: name,
        HTTPMethod: method.value,
        ApiURL: this.getUrlWithoutParams(url),
      }

      let payload = {
        ...this.payload,
        DocumentId: this.testData?.documentId || 0,
        SampleData: this.testData?.sampleData || {},
      }

      payload = this.cleanPayload(payload)

      try {
        const response = await this.$store.dispatch(
          'admin/testAPILookup',
          payload
        )
        const success = this.validateTestApiResponse(response)
        this.showSnackbar('API Lookup Test', success.message, success.isValid)

        if (!success.isValid) {
          this.$refs.apiLookupForm.setResponseData({
            httpStatusCode: (response?.status) ? response.status : '200',
            httpStatusMessage: (response?.statusText) ? response.statusText : 'OK',
            responseText: success.message,
            milliseconds: 0,
            size: 0,
          })
          return
        }

        if (success && response.Data?.ResponseText) {
          this.$refs.apiLookupForm.setResponseData({
            httpStatusCode: response.Data.HttpStatusCode,
            httpStatusMessage: response.Data.HttpStatusMessage,
            responseText: response.Data.ResponseText,
            milliseconds: response.Data.Milliseconds,
            size: response.Data.Size,
          })
          this.$refs.apiLookupForm.setResponseDataFieldKeys(
            response.Data.FieldKeys
          )
        }
      } catch (error) {
        console.error('API Lookup Save Error:', error)
        this.showSnackbar(
          'TEST API Lookup',
          'An unexpected error occurred',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    setRegistryDataInPayload(data) {
      this.payload = {
        ...this.payload,
        ApiLookupName: data.ApiLookupName,
        IndexFormId: data.IndexFormId,
        HTTPMethod: data.HTTPMethod,
        ApiURL: data.ApiURL,
        ApiRequestBodyType: data.ApiRequestBodyType,
        ApiRequestBody: data.ApiRequestBody,
        ApiResponse: data.ApiResponse,
        IsDefault: data.IsDefault,
        FirstRecordOnly: data.FirstRecordOnly,
        ApiAuthenticationId: data.ApiAuthenticationId,
        Headers: data.Headers,
        Parameters: data.Parameters,
        ApiRequestBodyFormData: data.ApiRequestBodyFormData,
      }
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: isSuccess,
        showSnackbar: true,
      }
    },
    syncTestRequestData(sampleData) {
      this.testData = sampleData
    },
    validateSave(method, name, url) {
      if (!name) {
        this.showSnackbar(
          'Validation Error',
          'API Lookup Name is required.',
          false
        )
        return false
      }

      if (!method.value) {
        this.showSnackbar('Validation Error', 'HTTP Method is required.', false)
        return false
      }

      if (!url) {
        this.showSnackbar('Validation Error', 'API URL is required.', false)
        return false
      }

      if (
        this.payload.ApiAuthenticationId === '' ||
        this.payload.ApiAuthenticationId === null ||
        this.payload.ApiAuthenticationId === undefined
      ) {
        this.showSnackbar(
          'Validation Error',
          'API Authentication is required.',
          false
        )
        return false
      }

      const hasEmptyParameter = this.payload.Parameters.some(param => {
        return !param.ParamKey || !param.ParamValue;
      });
      const hasEmptyHeader = this.payload.Headers.some(header => {
        return !header.HeaderKey || !header.HeaderValue;
      });

      if (hasEmptyParameter || hasEmptyHeader) {
        this.showSnackbar(
          'Validation Error',
          'All parameters and headers must have a key and value.',
          false
        );
        return false;
      }

      return true
    },
    validateTestApiResponse(response) {
      if (!(response?.Data?.FieldKeys)) {
        var errorMessage = 'The response is not valid.'
        if (response?.data?.ResultMessage) {
          errorMessage = response.data.ResultMessage
        }
        else if (response?.ResultMessage) {
          errorMessage = response.ResultMessage
        }
        return {
          isValid: false,
          message: errorMessage,
        }
      }

      if (!Array.isArray(response.Data.FieldKeys)) {
        return {
          isValid: false,
          message:
            'The Fields are missing in the resposne or the endpoint did not return values.',
        }
      }

      return {
        isValid: true,
        message:
          'It was executed successfully; you can now view and use the response data.',
      }
    },
  },
}
</script>
